import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAutomationSettings, updateAutomationSettings } from 'services/Settings';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

const DAYS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const isValidUiDay = (d) => ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].includes((d || '').toLowerCase());
const daysApiToUi = (arr = []) => arr.map((d) => (d ? d.slice(0, 3).toLowerCase() : '')).filter(isValidUiDay);
const daysUiToApi = (arr = []) =>
  arr.map((code) => DAYS_FULL.find((n) => n.slice(0, 3).toLowerCase() === (code || '').toLowerCase()) || code).filter((d) => !!d);

const parseTime = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map((v) => parseInt(v, 10));
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes || 0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

const formatTime = (date) => {
  if (!date) return '';
  const h = `${date.getHours()}`.padStart(2, '0');
  const m = `${date.getMinutes()}`.padStart(2, '0');
  return `${h}:${m}`;
};

export default function useAutomationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({});
  const { addToast } = useToast();

  const fetchSettings = useCallback(() => {
    setLoading(true);
    getAutomationSettings()
      .then((res) => {
        const settings = res?.data?.automation_settings || {};
        const secure = settings.secure_message || {};
        const text = settings.text_message || {};

        const mapped = {
          secure_message: {
            startTime: parseTime(secure?.time_range?.start),
            endTime: parseTime(secure?.time_range?.end),
            timeZone: secure?.timezone || '',
            days: daysApiToUi(secure?.days || []),
          },
          text_message: {
            startTime: parseTime(text?.time_range?.start),
            endTime: parseTime(text?.time_range?.end),
            timeZone: text?.timezone || '',
            days: daysApiToUi(text?.days || []),
          },
        };

        setData(mapped);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const buildPayload = useCallback(
    (key, values) => {
      const toApiSection = (uiValues = {}) => ({
        time_range: {
          start: formatTime(uiValues?.startTime),
          end: formatTime(uiValues?.endTime),
        },
        timezone: uiValues?.timeZone?.value || uiValues?.timeZone || '',
        days: daysUiToApi(uiValues?.days || []),
      });

      const otherKey = key === 'secure_message' ? 'text_message' : 'secure_message';
      const otherValues = data?.[otherKey] || {};

      const payload = {
        automation_settings: {
          secure_message: key === 'secure_message' ? toApiSection(values) : toApiSection(otherValues),
          text_message: key === 'text_message' ? toApiSection(values) : toApiSection(otherValues),
        },
      };

      return payload;
    },
    [data]
  );

  const saveSection = useCallback(
    (key, values) => {
      setSaving(true);
      const payload = buildPayload(key, values);
      return updateAutomationSettings(payload)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Automation settings updated');
          // Merge saved values locally
          setData((prev) => ({
            ...prev,
            [key]: {
              startTime: values?.startTime || null,
              endTime: values?.endTime || null,
              timeZone: values?.timeZone || '',
              days: values?.days || [],
            },
          }));
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to update automation settings');
        })
        .finally(() => setSaving(false));
    },
    [addToast, buildPayload]
  );

  const resetSection = useCallback(
    (key) => {
      setSaving(true);
      // Build payload where the selected section is undefined (omitted) and the other section remains as-is
      const toApiSection = (uiValues = {}) => ({
        time_range: {
          start: formatTime(uiValues?.startTime),
          end: formatTime(uiValues?.endTime),
        },
        timezone: uiValues?.timeZone?.value || uiValues?.timeZone || '',
        days: daysUiToApi(uiValues?.days || []),
      });

      const otherKey = key === 'secure_message' ? 'text_message' : 'secure_message';
      const otherValues = data?.[otherKey] || {};

      const payload = {
        automation_settings: {
          // Intentionally omit the resetting key by setting it to undefined; it will be dropped in JSON
          [key]: undefined,
          [otherKey]: toApiSection(otherValues),
        },
      };

      return updateAutomationSettings(payload)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Automation settings updated');
          // Clear the section locally
          setData((prev) => ({
            ...prev,
            [key]: {
              startTime: null,
              endTime: null,
              timeZone: '',
              days: [],
            },
          }));
        })
        .catch(() => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to update automation settings');
        })
        .finally(() => setSaving(false));
    },
    [addToast, data]
  );

  return useMemo(
    () => ({
      loading,
      saving,
      data,
      refetch: fetchSettings,
      saveSecureMessage: (values) => saveSection('secure_message', values),
      saveTextMessage: (values) => saveSection('text_message', values),
      resetSecureMessage: () => resetSection('secure_message'),
      resetTextMessage: () => resetSection('text_message'),
    }),
    [loading, saving, data, fetchSettings, saveSection, resetSection]
  );
}
